import { keepPreviousData, useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { ChevronLeft, LayoutGrid, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { api } from "../../api/client";
import type {
  App6EchelonCode,
  App6Entry,
  CommandRelation,
  CompositionCandidate,
  CompositionSide,
  CompositionWrite,
  UnitRole,
} from "../../api/types";
import { COMMAND_LABELS, COMMAND_RELATIONS, DEFAULT_COMMAND, isCompany } from "../../company";
import { Callout } from "../../components/Callout";
import { Dialog, DialogBody } from "../../components/Dialog";
import { Spinner } from "../../components/EmptyState";
import { Motion, Presence } from "../../components/presence";
import { Select } from "../../components/Select";
import { Badge, Button, Field, inputClass, SearchField, Segmented } from "../../components/ui";
import { useMediaQuery } from "../../components/useMediaQuery";
import { echelonNameFromSidc } from "../../echelon";
import { isNeutral } from "../../enemyDisplay";
import { HelpButton } from "../../help/HelpButton";
import { unitDesignation } from "../../i18n/designation";
import { k, t, tServer } from "../../i18n/i18n";
import { symbolSvg } from "../../map/units/symbols";
import { useDeployment } from "../../map/units/useDeployment";
import { specialtyLabel } from "../../missions";
import { useUi } from "../../state/ui";
import { App6Palette } from "../orbats/App6Palette";
import { bi, functionLabel, paletteOf } from "../orbats/app6";
import { sidcOf } from "../orbats/sidc";
import {
  type AddOrigin,
  admittedEchelons,
  candidateDetail,
  candidateFits,
  candidateWrite,
  cleanNumber,
  defaultEchelon,
  echelonFits,
  filterCandidates,
  fitsSentence,
  openingOrigin,
  paletteDefaultRole,
  paletteFits,
  paletteWrite,
  parentChoices,
  rootEchelon,
  unitEchelon,
} from "./addUnitModel";
import { defensiveRoles } from "../../map/units/propagation";
import { amiRoles, autoOption, companyRoles, defaultMark, roleOption, shownRole, UNIT_ROLES } from "./roles";
import { sideView } from "./rows";
import { useUnitActions } from "./useUnitActions";
import { Waiting } from "../../components/Waiting";

/** Fenêtre « Ajouter une unité » d'un camp (docs/18 §6.4) : palette APP-6 (unité générique : fonction, échelon, nom,
 *  numéros T et M), même ordre de bataille (unités absentes de la force, retirées comprises), catalogue, ordres de
 *  bataille construits visibles. Volet de confirmation : rôle dans ce dossier (défaut indiqué), formation de
 *  rattachement, effet attendu, « Ajouter au dossier ». L'unité est copiée dans le dossier : la source reste
 *  inchangée. */

const ORIGINS: { value: AddOrigin; label: string }[] = [
  { value: "app6", label: k("Palette APP-6") },
  { value: "same", label: k("Même ordre de bataille") },
  { value: "catalogue", label: k("Catalogue") },
  { value: "odb", label: k("Mes ordres de bataille") },
];

interface PaletteChoice {
  entry: App6Entry;
  app6: string;
}

export function AddUnitDialog({ side, onClose }: { side: CompositionSide; onClose: () => void }) {
  const exerciseId = useUi((s) => s.exerciseId);
  const { data: deployment } = useDeployment();
  const actions = useUnitActions();
  const wide = useMediaQuery("(min-width: 640px)");
  const view = sideView(deployment, side);
  const root = rootEchelon(view);
  // Côté ennemi face à l'ennemi générique (force non française, registre B39) : pas de catalogue (ses divisions sont
  // celles des ennemis des unités françaises ; le serveur le refuse).
  const catalogue = side === "AMI" || !isNeutral(deployment?.reference_enemy);
  const origins = catalogue ? ORIGINS : ORIGINS.filter((o) => o.value !== "catalogue");
  const [origin, setOriginState] = useState<AddOrigin>("same");
  // Onglet choisi par l'utilisateur : tant qu'il n'a rien choisi, la fenêtre s'ouvre sur un onglet qui propose des
  // unités (« Même ordre de bataille », sinon le catalogue).
  const [picked, setPicked] = useState(false);
  const setOrigin = (value: AddOrigin) => {
    setPicked(true);
    setOriginState(value);
  };
  const [query, setQuery] = useState("");
  const [candidate, setCandidate] = useState<CompositionCandidate | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [choice, setChoice] = useState<PaletteChoice | null>(null);
  const [echelon, setEchelon] = useState<App6EchelonCode | null>(null);
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [higher, setHigher] = useState("");
  const [role, setRole] = useState<UnitRole | null | undefined>(undefined);
  const [parent, setParent] = useState<string | null>(null);
  // Unité amie reçue par un S/GTIA (docs/21, décision 5) : TACON par défaut, OPCON au choix.
  const [command, setCommand] = useState<CommandRelation>(DEFAULT_COMMAND);
  const received = side === "AMI" && isCompany(deployment?.level);

  const vocabulary = useQuery({
    queryKey: ["odb-vocabulary", side],
    queryFn: () => api.odbVocabulary(side),
    staleTime: Infinity,
  });
  const palette = paletteOf(vocabulary.data);
  const allowed = side === "ENI" ? view?.out.echelons : null;
  const echelons = useMemo(() => admittedEchelons(palette, side, root, allowed), [palette, side, root, allowed]);

  const listing = useQuery({
    queryKey: ["composition-candidates", exerciseId, side, origin, query.trim()],
    queryFn: ({ signal }) =>
      api.compositionCandidates(exerciseId as string, { side, origin: origin === "app6" ? "same" : origin, q: query, limit: 50 }, signal),
    enabled: Boolean(exerciseId) && origin !== "app6",
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
  const items = filterCandidates(listing.data?.items ?? [], query);
  const sameEmpty = origin === "same" && !query.trim() && listing.data !== undefined && !listing.isPlaceholderData
    && !listing.data.refusal && listing.data.items.length === 0;
  useEffect(() => {
    if (!picked && sameEmpty) setOriginState(openingOrigin(0, catalogue));
  }, [picked, sameEmpty, catalogue]);

  const confirming = candidate !== null || (choice !== null && origin === "app6");
  const fallbackRole: UnitRole | null = candidate ? candidate.role_default : choice ? paletteDefaultRole(choice.entry, side) : null;
  const effectiveRole = role === undefined ? fallbackRole : role;
  const chosenEchelon = echelon ?? defaultEchelon(echelons);
  const fits = candidate
    ? candidateFits(candidate, view, effectiveRole, deployment?.level)
    : paletteFits(view, effectiveRole, chosenEchelon);
  const t1 = cleanNumber(number);
  const m1 = cleanNumber(higher);
  const numbersOk = t1 !== undefined && m1 !== undefined;

  const back = () => {
    setCandidate(null);
    setChoice(null);
    setRole(undefined);
  };
  const submit = () => {
    let body: CompositionWrite | null = null;
    if (candidate) body = candidateWrite(side, candidate, effectiveRole, parent);
    else if (choice && chosenEchelon && numbersOk)
      body = paletteWrite({
        side,
        entry: choice.entry,
        app6: choice.app6,
        echelon: chosenEchelon,
        role: effectiveRole,
        parent,
        name,
        number: t1 ?? null,
        higher: m1 ?? null,
      });
    if (!body) return;
    const write: CompositionWrite = received && body.op === "add" ? { ...body, command } : body;
    actions.guard(() => actions.writes.compose.mutate(write, { onSuccess: onClose }));
  };
  const restore = (c: CompositionCandidate) => {
    // Identité à rétablir donnée par le serveur (ennemi de référence : code générique de l'emplacement).
    const id = c.restore_key ?? (c.source.kind === "odb" ? c.source.unit : c.code);
    actions.guard(() => actions.writes.compose.mutate({ op: "restore", side, key: id }, { onSuccess: onClose }));
  };

  const addedEchelon = candidate
    ? ((candidate.echelon_code as App6EchelonCode | null) ?? unitEchelon({ sidc: candidate.sidc, echelon: candidate.echelon }))
    : chosenEchelon;
  const parents = parentChoices(view, addedEchelon);
  const digitsOnly = t("Chiffres seulement, huit au plus.");
  const sideText = side === "AMI" ? t("Ajouter une unité amie") : t("Ajouter une unité ennemie");
  // Niveau compagnie (docs/21 §4) : seulement les rôles des emplacements du S/GTIA, nommés comme dans son ordre.
  const companyLevel = side === "AMI" && isCompany(deployment?.level);
  // Rôles d'une défense (registre A126) : ni reconnaissance ; 1er et 2e échelons défensifs, renforts.
  const defensive = side === "AMI" && defensiveRoles(deployment);
  const roleChoices = amiRoles(companyLevel ? companyRoles(deployment?.company?.roles) : UNIT_ROLES, defensive);
  const roleSelect = (
    <Field label={t("Rôle dans ce dossier")} hint={roleHint(side, effectiveRole, fallbackRole, companyLevel, defensive)}>
      <Select
        className={inputClass}
        value={shownRole(effectiveRole, defensive) ?? ""}
        onChange={(e) => setRole((e.target.value || null) as UnitRole | null)}
      >
        {side === "AMI" && fallbackRole === null && (
          <option value="">{autoOption({ engine_role: null, support: null }, isCompany(deployment?.level), defensive)}</option>
        )}
        {roleChoices.map((r) => (
          <option key={r} value={r}>
            {roleOption(side, r, companyLevel, defensive).label}
            {r === fallbackRole ? ` (${defaultMark({ origin: candidate?.origin === "same" ? "odb" : "added" }).toLowerCase()})` : ""}
          </option>
        ))}
      </Select>
    </Field>
  );

  return (
    <>
      <Dialog
        title={sideText}
        subtitle={t("Dans ce dossier seulement : le catalogue et les ordres de bataille restent inchangés.")}
        onClose={onClose}
        actions={<HelpButton screen="carte" topic="carte.composition" buttonSize="sm" size={16} />}
        width={600}
        dirty={confirming}
        footer={
          confirming ? (
            <>
              <Button variant="secondary" onClick={back}>
                <ChevronLeft size={14} /> {t("Retour")}
              </Button>
              <Button
                onClick={submit}
                disabled={actions.writes.busy || (!candidate && (!chosenEchelon || !numbersOk))}
                blocked={!actions.writes.canEdit || Boolean(actions.pendingZone)}
                onBlocked={actions.refuse}
              >
                {actions.writes.compose.isPending ? <Waiting>{t("Ajout…")}</Waiting> : t("Ajouter au dossier")}
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={onClose}>
              {t("Annuler")}
            </Button>
          )
        }
      >
        {confirming ? (
          <Motion origin="fade">
            <DialogBody>
              <div className="flex min-h-8 items-center gap-2.5 rounded-lg bg-canvas px-2.5 py-1.5">
                <span
                  className="flex h-5 w-8 shrink-0 items-center justify-center [&>svg]:max-h-5 [&>svg]:max-w-8"
                  dangerouslySetInnerHTML={{
                    __html: symbolSvg(
                      candidate?.sidc ??
                        (choice && chosenEchelon
                          ? (sidcOf({ side, app6: choice.app6, echelon: chosenEchelon, hq: false, tf: false }) ?? "")
                          : ""),
                      16,
                    ),
                  }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-ui font-medium text-ink">
                    {candidate
                      ? unitDesignation(candidate.label)
                      : choice
                        ? (functionLabel(palette, choice.entry.key, choice.app6) ?? choice.entry.key)
                        : ""}
                  </span>
                  <span className="block truncate text-meta text-ink-muted">
                    {candidate
                      ? [unitDesignation(candidate.odb_name), unitDesignation(candidate.parent_label), candidateEchelon(candidate, side)].filter(Boolean).join(" · ")
                      : t("Unité générique de la palette APP-6")}
                  </span>
                  {candidate?.final_code && (
                    <span className="block text-meta text-ink-muted">
                      {t("Sera inscrite sous le code")}{" "}
                      <span className="font-mono text-ink tabular-nums">{candidate.final_code}</span>
                    </span>
                  )}
                </span>
              </div>
              {choice && !candidate && (
                <>
                  <Field label={t("Échelon")} hint={root ? t("Échelons inférieurs à celui de la force.") : undefined}>
                    <Select
                      className={inputClass}
                      value={chosenEchelon ?? ""}
                      onChange={(e) => setEchelon(e.target.value as App6EchelonCode)}
                    >
                      {echelons.map((e) => (
                        <option key={e.code} value={e.code}>
                          {bi(e)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label={t("Nom (facultatif)")}>
                    <input className={inputClass} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t("Numéro (T, facultatif)")} hint={t1 === undefined ? digitsOnly : undefined}>
                      <input className={inputClass} inputMode="numeric" value={number} onChange={(e) => setNumber(e.target.value)} />
                    </Field>
                    <Field label={t("Formation supérieure (M, facultatif)")} hint={m1 === undefined ? digitsOnly : undefined}>
                      <input className={inputClass} inputMode="numeric" value={higher} onChange={(e) => setHigher(e.target.value)} />
                    </Field>
                  </div>
                </>
              )}
              {roleSelect}
              {received && (
                <div>
                  <Segmented
                    label={t("Reçue en renforcement")}
                    options={COMMAND_RELATIONS.map((value) => ({ value, label: COMMAND_LABELS[value].label }))}
                    value={command}
                    onChange={setCommand}
                  />
                  <p className="mt-1 text-meta leading-snug text-ink-muted">{t(COMMAND_LABELS[command].hint)}</p>
                </div>
              )}
              {parents.length > 1 && (
                <Field label={t("Rattachée à")}>
                  <Select className={inputClass} value={parent ?? ""} onChange={(e) => setParent(e.target.value || null)}>
                    {parents.map((p) => (
                      <option key={p.id ?? ""} value={p.id ?? ""}>
                        {`${"  ".repeat(p.depth)}${unitDesignation(p.label)}`}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              <Callout tone="info">{fitsSentence(fits, effectiveRole, companyLevel)}</Callout>
            </DialogBody>
          </Motion>
        ) : (
          <div>
            <div className="sticky top-0 z-10 space-y-2 border-b border-line bg-surface px-4 py-2.5">
              {wide ? (
                <Segmented
                  ariaLabel={t("Provenance de l'unité")}
                  size="sm"
                  value={origin}
                  onChange={setOrigin}
                  options={origins.map((o) => ({ value: o.value, label: t(o.label) }))}
                />
              ) : (
                <Select
                  className={inputClass}
                  value={origin}
                  aria-label={t("Provenance de l'unité")}
                  onChange={(e) => setOrigin(e.target.value as AddOrigin)}
                >
                  {origins.map((o) => (
                    <option key={o.value} value={o.value}>
                      {t(o.label)}
                    </option>
                  ))}
                </Select>
              )}
              {origin !== "app6" && <SearchField value={query} onChange={setQuery} placeholder={t("Rechercher une unité")} />}
            </div>
            {origin === "app6" ? (
              <DialogBody>
                <p className="text-ui leading-relaxed text-ink-soft">
                  {t("Unité générique : choisissez sa fonction dans la palette des symboles OTAN, puis son échelon, son nom et ses numéros.")}
                </p>
                <Button
                  variant="secondary"
                  disabled={!vocabulary.data && vocabulary.isLoading}
                  onClick={() => setPaletteOpen(true)}
                  highlight={!choice}
                >
                  <LayoutGrid size={14} /> {t("Choisir dans la palette APP-6…")}
                </Button>
                {echelons.length === 0 && vocabulary.data && (
                  <Callout tone="warn">{t("Aucun échelon inférieur à celui de la force n'est proposé par la palette.")}</Callout>
                )}
              </DialogBody>
            ) : listing.isLoading ? (
              <div className="flex items-center gap-2 px-4 py-4 text-ui text-ink-muted">
                <Spinner decorative /> {<Waiting>{t("Recherche des unités…")}</Waiting>}
              </div>
            ) : listing.error ? (
              <div className="px-4 py-4">
                <Callout tone="danger">
                  {listing.error instanceof Error ? listing.error.message : t("Liste des unités indisponible.")}
                </Callout>
              </div>
            ) : listing.data?.refusal ? (
              <div className="px-4 py-4">
                <Callout tone="info">{tServer(listing.data.refusal)}</Callout>
              </div>
            ) : items.length === 0 ? (
              <p className="px-4 py-4 text-ui text-ink-muted">
                {query.trim()
                  ? t("Aucune unité ne correspond à « {query} ».", { query: query.trim() })
                  : origin === "same"
                    ? t("Toutes les unités de l'ordre de bataille sont déjà dans le dossier : choisissez Catalogue, Mes ordres de bataille ou la Palette APP-6.")
                    : t("Aucune unité à proposer ici.")}
              </p>
            ) : (
              <ul className="space-y-px p-2">
                {items.map((c) => {
                  const fitsRoot = echelonFits(c.echelon_code, root);
                  const blocked = c.state === "present" || !fitsRoot;
                  return (
                    <li key={`${c.source.kind}:${c.source.kind === "odb" ? `${c.source.odb}/${c.source.unit}` : c.code}`}>
                      <div
                        className={clsx(
                          "flex min-h-8 items-center gap-2 rounded-lg px-2 py-1.5 transition-colors",
                          !blocked && c.state !== "removed" && "hover:bg-hover",
                        )}
                      >
                        <button
                          type="button"
                          disabled={blocked || c.state === "removed"}
                          onClick={() => {
                            setCandidate(c);
                            setRole(undefined);
                          }}
                          className="focus-inset flex min-w-0 flex-1 items-center gap-2.5 text-left disabled:cursor-default"
                          title={
                            !fitsRoot
                              ? t("Échelon trop élevé pour ce dossier")
                              : c.state === "present"
                                ? t("Déjà dans le dossier")
                                : undefined
                          }
                        >
                          <span
                            className={clsx(
                              "flex h-4 w-6 shrink-0 items-center justify-center [&>svg]:max-h-4 [&>svg]:max-w-6",
                              blocked && "opacity-40",
                            )}
                            dangerouslySetInnerHTML={{ __html: symbolSvg(c.sidc, 13) }}
                          />
                          <span className="min-w-0 flex-1">
                            <span
                              className={clsx(
                                "block text-ui leading-snug font-medium break-words",
                                blocked ? "text-ink-muted" : "text-ink",
                              )}
                            >
                              {unitDesignation(c.label)}
                              {c.label !== c.code && (
                                // Code, comme dans l'ordre de bataille : deux « Compagnie 1 » se distinguent.
                                <span className="ml-1.5 font-mono text-meta font-normal text-ink-muted tabular-nums">{unitDesignation(c.code)}</span>
                              )}
                            </span>
                            <span className="block text-meta leading-snug break-words text-ink-muted">
                              {candidateDetail(c, candidateEchelon(c, side), c.specialty ? specialtyLabel(c.specialty) : null)}
                            </span>
                          </span>
                        </button>
                        {c.state === "present" && <Badge size="sm">{t("Déjà dans le dossier")}</Badge>}
                        {c.state === "removed" && (
                          <>
                            <Badge dot size="sm" tone="warn">
                              {t("Retirée du dossier")}
                            </Badge>
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={actions.writes.busy}
                              blocked={!actions.writes.canEdit || Boolean(actions.pendingZone)}
                              onBlocked={actions.refuse}
                              onClick={() => restore(c)}
                            >
                              <RotateCcw size={14} /> {t("Rétablir")}
                            </Button>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
                {listing.data?.truncated && (
                  <li className="px-2 pt-1.5 pb-1 text-meta text-ink-muted">{t("Liste écourtée : précisez la recherche.")}</li>
                )}
              </ul>
            )}
          </div>
        )}
      </Dialog>
      {/* Palette hors de la fenêtre : une fenêtre fixe ne s'ouvre pas dans une autre qui s'anime. */}
      <Presence show={paletteOpen}>
        {paletteOpen && (
          <App6Palette
            palette={palette}
            unit={{
              side,
              echelon: chosenEchelon ?? "15",
              hq: false,
              tf: false,
              number: t1 ?? null,
              higher: m1 ?? null,
              fonction: choice?.entry.key ?? null,
              app6: choice?.app6 ?? null,
            }}
            onChoose={(entry, app6) => {
              setChoice({ entry, app6 });
              setRole(undefined);
              setPaletteOpen(false);
            }}
            onClose={() => setPaletteOpen(false)}
          />
        )}
      </Presence>
    </>
  );
}

/** Échelon d'une unité candidate, d'après son symbole (sinon l'échelon du moteur). */
function candidateEchelon(c: CompositionCandidate, side: CompositionSide): string | null {
  return echelonNameFromSidc(c.sidc, c.arm, side === "AMI")?.toLowerCase() ?? c.echelon?.toLowerCase() ?? null;
}

/** Aide sous le sélecteur de rôle : effet du rôle choisi (ami) ou rappel du rôle par défaut. */
function roleHint(
  side: CompositionSide,
  role: UnitRole | null,
  fallback: UnitRole | null,
  company = false,
  defensive = false,
): string | undefined {
  if (side === "AMI" && role) return roleOption(side, role, company, defensive).hint ?? undefined;
  if (role !== fallback) return t("Différent du rôle par défaut : il sera retenu pour ce dossier.");
  return undefined;
}
